// File bridge backed by Supabase tables (web_scripts +
// web_script_versions + web_drafts + web_recents).
//
// Two modes:
//   - Supabase configured + signed in: real reads/writes against
//     Postgres. The cloudId returned to the renderer is the
//     web_scripts.id UUID — opaque from the renderer's POV, matching
//     Electron's behavior where filePath was an opaque OS string.
//   - Supabase missing or signed out: falls back to the M1 bundled
//     97-page fixture so `npm run web:dev` produces something usable
//     before the developer configures auth. Saves are no-ops in that
//     mode.

import { buildScript, parseFountain } from '../parsers/fountain.js';
import { createProjectFromTemplate, WEB_FORMATS } from '../parsers/scaffold.js';
import { setActiveScriptId } from './active-script.js';
import fixtureText from '../../../test/fixtures/20th_century_women.fountain?raw';

const FIXTURE_ID = 'fixture:20th-century-women';
const FIXTURE_TITLE = '20th Century Women';

async function currentUserId(supabase) {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data?.session?.user?.id || null;
}

async function sha256Hex(text) {
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest('SHA-256', enc.encode(text));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function fetchLatestVersion(supabase, scriptId, draftId) {
  let query = supabase
    .from('web_script_versions')
    .select('id, content, created_at')
    .eq('script_id', scriptId)
    .order('created_at', { ascending: false })
    .limit(1);
  if (draftId) query = query.eq('draft_id', draftId);
  const { data, error } = await query;
  if (error) throw error;
  return data && data[0];
}

async function loadScriptById(supabase, scriptId, { emitTo } = {}) {
  const { data: row, error } = await supabase
    .from('web_scripts')
    .select('id, title, author, format, active_draft_id, created_at, updated_at')
    .eq('id', scriptId)
    .single();
  if (error) throw error;

  const version = await fetchLatestVersion(supabase, scriptId, row.active_draft_id || null);
  const content = (version && version.content) || '';
  const script = buildScript(content, { filePath: scriptId, title: row.title });
  setActiveScriptId(scriptId);
  if (emitTo) emitTo(script);
  // Bump recents (best-effort; ignore failures).
  const userId = await currentUserId(supabase);
  if (userId) {
    await supabase
      .from('web_recents')
      .upsert(
        { user_id: userId, script_id: scriptId, opened_at: new Date().toISOString() },
        { onConflict: 'user_id,script_id' },
      );
  }
  return script;
}

async function listUserScripts(supabase, limit = 50) {
  const userId = await currentUserId(supabase);
  if (!userId) return [];
  const { data, error } = await supabase
    .from('web_scripts')
    .select('id, title, format, updated_at')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

async function createScript(supabase, { title, author, format, fountainSeed }) {
  const userId = await currentUserId(supabase);
  if (!userId) throw new Error('not signed in');

  const { data: script, error: scriptErr } = await supabase
    .from('web_scripts')
    .insert({ user_id: userId, title, author: author || null, format })
    .select()
    .single();
  if (scriptErr) throw scriptErr;

  const { data: draft, error: draftErr } = await supabase
    .from('web_drafts')
    .insert({
      user_id: userId,
      script_id: script.id,
      name: 'main',
      display_name: 'Main',
    })
    .select()
    .single();
  if (draftErr) throw draftErr;

  await supabase.from('web_scripts').update({ active_draft_id: draft.id }).eq('id', script.id);

  const content = String(fountainSeed || '');
  const sha = await sha256Hex(content);
  await supabase.from('web_script_versions').insert({
    user_id: userId,
    script_id: script.id,
    draft_id: draft.id,
    content,
    content_sha256: sha,
    label: 'initial',
    word_count: content.split(/\s+/).filter(Boolean).length,
    byte_count: content.length,
    manual: true,
  });

  return { scriptId: script.id, draftId: draft.id };
}

function fixtureScript(bus) {
  const s = buildScript(fixtureText, { filePath: FIXTURE_ID, title: FIXTURE_TITLE });
  setActiveScriptId(FIXTURE_ID);
  if (bus) bus.emit('script:loaded', s);
  return s;
}

export function buildFileHandlers({ supabase, bus, runPrelistMigration }) {
  async function openOrCreateFirstScript() {
    const scripts = await listUserScripts(supabase, 1);
    if (scripts.length > 0) {
      return loadScriptById(supabase, scripts[0].id, {
        emitTo: (s) => bus.emit('script:loaded', s),
      });
    }
    // No scripts yet — bootstrap a blank one so the writer lands in
    // the editor instead of getting silently ignored.
    const { fountain } = createProjectFromTemplate('blank', { title: 'Untitled', author: '' });
    const { scriptId } = await createScript(supabase, {
      title: 'Untitled',
      author: '',
      format: 'fountain',
      fountainSeed: fountain,
    });
    return loadScriptById(supabase, scriptId, { emitTo: (s) => bus.emit('script:loaded', s) });
  }

  return {
    async openScript() {
      // On Electron, `openScript` pops a native OS file dialog. On web
      // there's no file dialog — the equivalent action is "show me my
      // cloud projects." The renderer already owns a polished project
      // switcher UI (#btn-project-switcher → toggleProjectSwitcher);
      // trigger it via a click on the button instead of reinventing
      // the modal here. Falls through to the dev fixture only when
      // there's no Supabase configured at all (local-dev path).
      if (!supabase) return fixtureScript(bus);
      try {
        const trigger = document.getElementById('btn-project-switcher');
        if (trigger && typeof trigger.click === 'function') {
          trigger.click();
          return { ok: true };
        }
        // Fallback: emit the same menu channel the keyboard shortcut
        // fires, which the renderer also wires to toggleProjectSwitcher.
        bus.emit('menu:open-cloud');
        return { ok: true };
      } catch (err) {
        console.error('[bridge.file.openScript]', err);
        return { ok: false, error: err.message };
      }
    },

    async loadFile(scriptId) {
      // M1 fixture path / signed-out path: ignore the id and return
      // the fixture so the renderer still has something to render.
      if (!supabase || !scriptId || scriptId === FIXTURE_ID || scriptId.startsWith('fixture:')) {
        return fixtureScript(bus);
      }
      try {
        return await loadScriptById(supabase, scriptId, {
          emitTo: (s) => bus.emit('script:loaded', s),
        });
      } catch (err) {
        return { error: err.message };
      }
    },

    async saveFile({ path: scriptId, content } = {}) {
      if (!supabase) return { ok: true, skipped: true };
      const userId = await currentUserId(supabase);
      if (!userId) return { ok: true, skipped: true };
      if (!scriptId || scriptId.startsWith('fixture:')) {
        // Fixture is read-only; pretend it saved.
        return { ok: true, skipped: true };
      }
      try {
        const sha = await sha256Hex(String(content || ''));
        // Read active draft.
        const { data: scriptRow, error: srErr } = await supabase
          .from('web_scripts')
          .select('active_draft_id')
          .eq('id', scriptId)
          .single();
        if (srErr) throw srErr;
        const draftId = scriptRow?.active_draft_id;
        if (!draftId) throw new Error('script has no active draft');

        const { error: insErr } = await supabase.from('web_script_versions').insert({
          user_id: userId,
          script_id: scriptId,
          draft_id: draftId,
          content: String(content || ''),
          content_sha256: sha,
          word_count: String(content || '')
            .split(/\s+/)
            .filter(Boolean).length,
          byte_count: String(content || '').length,
          manual: false,
        });
        if (insErr) throw insErr;

        // Bump updated_at via trigger (UPDATE on parent row).
        await supabase
          .from('web_scripts')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', scriptId);
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },

    async saveAs({ content, defaultName } = {}) {
      // On Electron, Save As pops a native Save dialog and writes to
      // disk. The web equivalent is a Blob download — that's the only
      // way to give the user a real file in a browser without the
      // File System Access API (Chromium-only, limited).
      //
      // Called by the renderer's saveDocumentAs() (menu:save-as,
      // ⇧⌘S) for local-only drafts AND, indirectly, by saveCheckpoint
      // when ⌘S fires on a draft without a filePath. Also called by
      // saveCopyOfCloudDraftToMac for non-cloud drafts (cloud-bound
      // drafts go through cloudSaveLocalCopyAs in cloud.js).
      try {
        const safeBase =
          String(defaultName || 'Untitled')
            .replace(/\.(fountain|fdx|txt|pdf)$/i, '')
            .replace(/[^a-z0-9._-]+/gi, '_')
            .replace(/^_+|_+$/g, '') || 'Untitled';
        const filename = `${safeBase}.fountain`;
        const blob = new Blob([String(content || '')], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        // Return a synthetic filePath the renderer can stash; it's
        // never used to load back from disk, but the renderer reads
        // `res.filePath` to update the title bar after Save As.
        return { ok: true, filePath: `download://${filename}` };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },

    // Renderer's exportAs() pre-serializes the script body into the
    // requested format (FDX / Fountain / RTF / HTML / Text / CSV /
    // JSON) and hands us the bytes. On Electron this hits an OS
    // Save dialog and writes to disk; on web it triggers a Blob
    // download. The bytes are already correctly formatted —
    // we just need to pick the right MIME + filename and `<a>` it.
    async exportAs({ content, defaultName, format } = {}) {
      if (typeof content !== 'string' && !(content instanceof Uint8Array)) {
        return { ok: false, error: 'no_content' };
      }
      const mimeFor = (fmt) => {
        switch (fmt) {
          case 'fdx':
          case 'fdx-no-notes':
            return 'application/xml';
          case 'fountain':
          case 'text':
            return 'text/plain';
          case 'rtf':
            return 'application/rtf';
          case 'html':
            return 'text/html';
          case 'csv':
            return 'text/csv';
          case 'json':
            return 'application/json';
          default:
            return 'application/octet-stream';
        }
      };
      try {
        const safeName =
          String(defaultName || `screenplay.${format || 'txt'}`)
            .replace(/[^a-z0-9._-]+/gi, '_')
            .replace(/^_+|_+$/g, '') || `screenplay.${format || 'txt'}`;
        const blob = new Blob([content], { type: mimeFor(format) });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = safeName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return { ok: true, path: safeName };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },

    async newFromTemplate(args = {}) {
      // Renderer's `createNewProject` calls this on the non-cloud
      // path (free / signed-out / anonymous). On Electron it writes
      // the template to disk and dispatches; on web we build the
      // template in-memory and emit `script:loaded` so the editor
      // mounts it. Persistence is local-only (y-indexeddb via the
      // doc-store) — the user can later "Move to cloud" when they
      // upgrade.
      try {
        const { format, title, author, series, episode } = args;
        const { fountain } = createProjectFromTemplate(format || 'blank', {
          title: String(title || 'Untitled'),
          author: String(author || ''),
          series: series ? String(series) : '',
          episode: episode ? String(episode) : '',
        });
        const synthId = `local:${(crypto.randomUUID && crypto.randomUUID()) || Date.now()}`;
        const script = buildScript(fountain, {
          filePath: synthId,
          title: String(title || 'Untitled'),
          format: 'fountain',
        });
        setActiveScriptId(synthId);
        bus.emit('script:loaded', script);
        return { ok: true, path: synthId };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },

    async listFormats() {
      // Renderer (src/renderer/js/app.js:2798) calls `formats.map(...)`
      // on the return value directly, so this must be a bare array
      // (matches src/main/main.js's `file:list-formats` handler).
      return WEB_FORMATS;
    },

    // Build a Fountain template payload without dispatching it as
    // the active script. Used by the cloud migration helper. Mirrors
    // src/main/main.js's `template:build` handler shape.
    async buildTemplate({ format = 'feature', fields = {} } = {}) {
      try {
        const out = createProjectFromTemplate(format, fields);
        const parsed = parseFountain(out.fountain);
        return { ok: true, fountain: out.fountain, format: out.format, parsed };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },

    // Parse a fountain/FDX/PDF blob without dispatching it. Returns
    // the same Script object buildScript() produces. The web build
    // doesn't accept absolute filesystem paths (no fs); the caller
    // must hand us the text directly via `parseContent`.
    async parseFile(_filePath) {
      return { ok: false, error: 'parseFile by absolute path is not available on web' };
    },
    async parseContent({ text, extension, filePath } = {}) {
      if (typeof text !== 'string') return { ok: false, error: 'text required' };
      try {
        const { parseAny } = await import('../parsers/index.js');
        const ext = (extension || '.fountain').toLowerCase();
        const script = await parseAny({ text, extension: ext, filePath });
        return { ok: true, script };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },

    async openFolder() {
      // "Folder" on Electron = a directory of scripts. On web there
      // isn't a directory equivalent; treat it as "show me my
      // scripts" by emitting onFolderLoaded with the full cloud
      // listing. If there are none, no-op.
      if (!supabase) return { ok: false, error: 'Supabase not configured' };
      try {
        const scripts = await listUserScripts(supabase, 50);
        bus.emit('folder:loaded', {
          path: 'cloud://you',
          files: scripts.map((s) => ({
            filePath: s.id,
            name: s.title,
            updatedAt: s.updated_at,
          })),
        });
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },

    // Renderer reads `r.projects[*].{cloudId, name, lastBackupAt, bytesUsed}`
    // — same shape Electron's `backup:list-cloud` returns (see
    // src/renderer/js/app.js:2400, 6181). On web, every cloud script
    // IS the backup, so we surface web_scripts rows here.
    async backupListCloud() {
      if (!supabase) return { ok: true, projects: [] };
      try {
        const userId = await currentUserId(supabase);
        if (!userId) return { ok: false, error: 'not signed in', code: 'unauthorized' };
        // First time the welcome list is queried after sign-in, kick
        // off a one-shot import of any Electron-backed projects so a
        // returning desktop user sees their existing work without
        // needing to click a button. ensureElectronImportOnce is
        // idempotent and silently no-ops if the Edge Function isn't
        // deployed.
        if (typeof runPrelistMigration === 'function') {
          try {
            await runPrelistMigration();
          } catch (err) {
            console.warn('[bridge.file] electron migration trigger failed', err);
          }
        }
        const { data, error } = await supabase
          .from('web_scripts')
          .select('id, title, updated_at')
          .eq('user_id', userId)
          .order('updated_at', { ascending: false })
          .limit(100);
        if (error) throw error;
        const projects = (data || []).map((s) => ({
          cloudId: s.id,
          name: s.title || 'Untitled',
          lastBackupAt: s.updated_at,
          bytesUsed: 0,
        }));
        return { ok: true, projects };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },

    // On Electron, `backupRestore` pulls a git bundle from Supabase
    // storage and writes it to a local file path the renderer then
    // loadFile()s. On web every script already lives in Postgres, so
    // "restore" is a no-op handshake — we return the script id as
    // filePath and let the renderer's existing loadFile flow take it
    // from there. Renderer call site: src/renderer/js/app.js:6118.
    async backupRestore(cloudId /* , name */) {
      if (!cloudId) return { ok: false, error: 'cloudId required' };
      return { ok: true, filePath: cloudId, alreadyRestored: true };
    },

    async revert({ path: scriptId } = {}) {
      // Reload the latest persisted version into the editor.
      if (!supabase) return { ok: false, error: 'Supabase not configured' };
      try {
        await loadScriptById(supabase, scriptId, {
          emitTo: (s) => bus.emit('script:loaded', s),
        });
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },
  };
}
